/**
 * Notifications Page (formerly "Org Themes")
 *
 * Allows org users (ORG_OWNER, WORKSPACE_ADMIN) to create and manage
 * private query-lens themes: name, keywords, seed text, and optional filters.
 *
 * These are never materialized into chunk_themes — they are evaluated
 * dynamically at retrieval time by WorkspaceScopeResolver.
 *
 * NOTE: only the page name/route/nav label changed — the underlying
 * org_themes API and data model (shared with ScopeSelector and the
 * Topics/Dept. page) are unchanged.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import { usePageTitle } from '@/hooks/usePageTitle';

interface OrgThemeDefinition {
  keywords?: string[];
  seed_text?: string;
  filters?: Record<string, unknown>;
}

interface OrgTheme {
  id: string;
  name: string;
  definition: OrgThemeDefinition;
  created_at: string;
}

export const NotificationsPage: React.FC = () => {
  usePageTitle('Notifications');
  const [themes, setThemes] = useState<OrgTheme[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<OrgTheme | null>(null);
  const [formName, setFormName] = useState('');
  const [formKeywords, setFormKeywords] = useState(''); // comma-separated
  const [formSeedText, setFormSeedText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete state
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      const res = await apiFetch('/api/org-themes');
      if (!res.ok) throw new Error('Failed to load themes');
      const data = await res.json();
      setThemes(data.data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading themes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormKeywords('');
    setFormSeedText('');
    setSaveError(null);
    setDialogOpen(true);
  };

  const openEdit = (theme: OrgTheme) => {
    setEditing(theme);
    setFormName(theme.name);
    setFormKeywords((theme.definition.keywords ?? []).join(', '));
    setFormSeedText(theme.definition.seed_text ?? '');
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) return;
    setSaving(true);
    setSaveError(null);

    const definition: OrgThemeDefinition = {
      keywords: formKeywords
        .split(',')
        .map((k) => k.trim())
        .filter(Boolean),
      seed_text: formSeedText.trim() || undefined,
      filters: editing?.definition.filters ?? {},
    };

    try {
      if (editing) {
        const res = await apiFetch(`/api/org-themes/${editing.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ name: formName.trim(), definition }),
        });
        if (!res.ok) throw new Error('Failed to update theme');
      } else {
        const res = await apiFetch('/api/org-themes', {
          method: 'POST',
          body: JSON.stringify({ name: formName.trim(), definition }),
        });
        if (!res.ok) throw new Error('Failed to create theme');
      }
      setDialogOpen(false);
      void load();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try {
      const res = await apiFetch(`/api/org-themes/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete');
      setThemes((prev) => prev.filter((t) => t.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Notifications</h1>
          <p className="mt-1 text-gray-600">
            Private query-lens themes — keywords + seed text evaluated at
            retrieval time
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          New Theme
        </Button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        </div>
      ) : themes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Tag className="mb-4 h-12 w-12 text-gray-400" />
            <h3 className="mb-2 text-lg font-medium text-gray-900">
              No org themes yet
            </h3>
            <p className="mb-4 text-sm text-gray-500">
              Create a private query-lens to filter retrieval by your own topics
            </p>
            <Button onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              New Theme
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {themes.map((theme) => (
            <Card key={theme.id}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{theme.name}</CardTitle>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => openEdit(theme)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:bg-red-50 hover:text-red-700"
                      disabled={deleting === theme.id}
                      onClick={() => handleDelete(theme.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {(theme.definition.keywords ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {(theme.definition.keywords ?? []).map((kw) => (
                      <Badge key={kw} variant="secondary" className="text-xs">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                )}
                {theme.definition.seed_text && (
                  <p className="text-sm italic text-gray-500">
                    &ldquo;{theme.definition.seed_text}&rdquo;
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* ── Create / Edit Dialog ──────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit Theme' : 'New Org Theme'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4 pt-2">
            <div className="space-y-1.5">
              <Label htmlFor="theme-name">Name</Label>
              <Input
                id="theme-name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g. Stormwater Policy"
                required
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="theme-keywords">
                Keywords{' '}
                <span className="font-normal text-gray-400">
                  (comma-separated)
                </span>
              </Label>
              <Input
                id="theme-keywords"
                value={formKeywords}
                onChange={(e) => setFormKeywords(e.target.value)}
                placeholder="e.g. stormwater, drainage, NPDES, runoff"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="theme-seed">
                Seed text{' '}
                <span className="font-normal text-gray-400">(optional)</span>
              </Label>
              <Input
                id="theme-seed"
                value={formSeedText}
                onChange={(e) => setFormSeedText(e.target.value)}
                placeholder="e.g. municipal separate storm sewer system compliance"
              />
              <p className="text-xs text-gray-500">
                Used to compute a query embedding centroid at retrieval time.
              </p>
            </div>

            {saveError && <p className="text-sm text-red-600">{saveError}</p>}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving || !formName.trim()}>
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Theme'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
